import { Flag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import CollapsibleSection from '../ui/CollapsibleSection';
import { formatDate } from '../utils/dateFormatter';
import {
    STATE_LABELS,
    VERDICT_LABELS,
    categoryLabel,
    needsAttention,
    outcomeLabel,
    scorePercent,
    type ModerationBan,
    type ModerationScan,
    type ModerationState,
} from './moderation';

// The moderation block the session APIs return. The list sends the state
// only; the session detail composes the state, the review run's verdict and
// the scan that flagged the conversation.
export interface SessionModerationData {
    state: ModerationState;
    scan?: ModerationScan | null;
    review?: {
        run_id?: string | null;
        status?: string | null;
        verdict?: 'bad_faith' | 'good_faith' | 'unclear' | null;
        category?: string | null;
        summary?: string | null;
        ban?: ModerationBan | null;
        not_banned_reason?: string | null;
        at?: string | null;
    } | null;
    cleared_at?: string | null;
    flagged_at?: string | null;
    banned_until?: string | null;
    // The 24-hour ban has lapsed; the state stays for the record.
    ban_expired?: boolean;
}

export function isSessionFlagged(moderation?: Pick<SessionModerationData, 'state'> | null): boolean {
    return needsAttention(moderation?.state);
}

export function ModerationBadge({ moderation }: { moderation?: Pick<SessionModerationData, 'state'> | null }) {
    if (!moderation || !isSessionFlagged(moderation)) return null;
    return (
        <Badge
            variant="outline"
            className="gap-1 border-orange-500/60 text-orange-600 dark:text-orange-400 text-[10px] px-1.5 py-0"
            title={STATE_LABELS[moderation.state] || moderation.state}
        >
            <Flag className="h-3 w-3" />
            Flagged
        </Badge>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="py-2.5 flex items-start justify-between gap-4">
            <span className="text-sm font-medium text-muted-foreground shrink-0">{label}</span>
            <span className="text-sm text-right">{children}</span>
        </div>
    );
}

interface ModerationSectionProps {
    moderation?: SessionModerationData | null;
    onClear: () => void;
    clearing?: boolean;
}

export function ModerationSection({ moderation, onClear, clearing }: ModerationSectionProps) {
    if (!moderation?.state) return null;

    const review = moderation.review;
    const scan = moderation.scan;
    const hasVerdict = !!review?.verdict;
    const category = review?.category || scan?.categories?.[0];
    const percent = scorePercent(scan?.score);
    const when = review?.at || moderation.flagged_at || scan?.at;
    // A live ban is lifted with Unban Visitor, which also lifts this state.
    const banActive = moderation.state === 'banned' && !moderation.ban_expired;
    const canClear = isSessionFlagged(moderation) && !banActive;
    const statusLabel = moderation.ban_expired ? 'Ban expired' : STATE_LABELS[moderation.state] || moderation.state;

    return (
        <CollapsibleSection
            title="Moderation"
            icon={<Flag className="w-4 h-4 text-orange-500" />}
            storageKey="session-details:Moderation"
        >
            <div className="divide-y">
                <Row label="Status">
                    <Badge
                        variant="outline"
                        className={
                            isSessionFlagged(moderation)
                                ? 'text-xs border-orange-500/60 text-orange-600 dark:text-orange-400'
                                : 'text-xs text-muted-foreground'
                        }
                    >
                        {statusLabel}
                    </Badge>
                </Row>
                {banActive && moderation.banned_until && (
                    <Row label="Ban ends">{formatDate(moderation.banned_until)}</Row>
                )}
                {hasVerdict && <Row label="Verdict">{VERDICT_LABELS[review!.verdict as string] || review!.verdict}</Row>}
                {category && category !== 'none' && <Row label="Category">{categoryLabel(category)}</Row>}
                {hasVerdict && (
                    <Row label="What happened">{outcomeLabel(review?.ban, review?.not_banned_reason)}</Row>
                )}
                {percent && <Row label="Scan score">{percent}</Row>}
                {when && <Row label={review?.at ? 'Reviewed at' : 'Flagged at'}>{formatDate(when)}</Row>}
            </div>
            {(review?.summary || scan?.reason) && (
                <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">
                    {review?.summary || scan?.reason}
                </p>
            )}
            {canClear && (
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={onClear}
                    disabled={clearing}
                >
                    Clear flag
                </Button>
            )}
        </CollapsibleSection>
    );
}
